import { useEffect, useRef, useState } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';

interface AskJeffNoteDialogProps {
  isOpen: boolean;
  /** The note already on record, or null when the hand has just been flagged. */
  note: string | null;
  /** True once the hand is flagged — changes the wording from "ask" to "edit". */
  isFlagged: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: (note: string | null) => void;
  onCancel: () => void;
}

/**
 * The note the Super Editor writes when sending a hand to Jeff (feature 025). Same modal shape
 * as ConfirmDialog — backdrop, Escape to close, locked body scroll — but with a textarea, since
 * the note IS the point of the dialog rather than a confirmation.
 */
export function AskJeffNoteDialog({
  isOpen,
  note,
  isFlagged,
  isSaving,
  error,
  onSave,
  onCancel,
}: AskJeffNoteDialogProps) {
  const [draft, setDraft] = useState(note ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEscapeClose(() => { if (!isSaving) onCancel(); }, isOpen);

  // Re-seed the draft each time the dialog opens, so re-opening after a cancel shows what is
  // actually on record rather than the abandoned edit.
  useEffect(() => {
    if (isOpen) {
      setDraft(note ?? '');
      textareaRef.current?.focus();
    }
  }, [isOpen, note]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-jeff-dialog-title"
      data-testid="ask-jeff-dialog"
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={!isSaving ? onCancel : undefined}
        aria-hidden="true"
      />

      <div className="relative bg-card rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <h2 id="ask-jeff-dialog-title" className="text-lg font-semibold text-ink mb-1">
          {isFlagged ? 'Notes for Jeff' : 'Ask Jeff about this hand'}
        </h2>
        <p className="text-sm text-ink-soft mb-4">
          {isFlagged
            ? 'This hand is already flagged for Jeff. Update your notes below.'
            : 'Describe your doubt. Jeff will see this hand with your notes when he reviews it.'}
        </p>

        <label htmlFor="ask-jeff-note" className="block text-sm font-medium text-ink mb-1">
          Your notes <span className="font-normal text-ink-soft">(optional)</span>
        </label>
        <textarea
          id="ask-jeff-note"
          ref={textareaRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={5}
          disabled={isSaving}
          data-testid="ask-jeff-note-input"
          className="w-full rounded-md border border-line-strong bg-paper px-3 py-2 text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:opacity-60"
          placeholder="e.g. Would a 2NT rebid show this shape, or is 3C better here?"
        />

        {error && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="rounded-md border border-line-strong px-4 py-2 font-medium text-ink-soft hover:text-ink disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft.trim() === '' ? null : draft.trim())}
            disabled={isSaving}
            data-testid="ask-jeff-save"
            className="rounded-md bg-primary-600 px-4 py-2 font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : isFlagged ? 'Save notes' : 'Ask Jeff'}
          </button>
        </div>
      </div>
    </div>
  );
}
